// ============================================================
// FRONTEND: src/features/reports/components/SummaryCard.tsx
// ============================================================

import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { clsx } from 'clsx';

interface SummaryCardProps {
    label: string;
    value: string;
    icon: React.ElementType;
    gradient: string;
    comparison?: {
        value: number;
        label: string;
        isPositive: boolean;
    };
    subtitle?: string;
    onClick?: () => void;
}

export function SummaryCard({ label, value, icon: Icon, gradient, comparison, subtitle, onClick }: SummaryCardProps) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            onClick={onClick}
            className={clsx(
                'bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all',
                onClick && 'cursor-pointer'
            )}
        >
            <div className="flex items-start justify-between">
                <div className="space-y-1 flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
                    {subtitle && (
                        <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
                    )}
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>

            {comparison && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-1.5">
                        {comparison.isPositive ? (
                            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                            <TrendingDown className="w-3.5 h-3.5 text-rose-500" />
                        )}
                        <span className={clsx(
                            'text-xs font-bold',
                            comparison.isPositive ? 'text-emerald-600' : 'text-rose-600'
                        )}>
                            {comparison.value.toFixed(1)}%
                        </span>
                        <span className="text-xs text-slate-400 ml-1">{comparison.label}</span>
                    </div>
                </div>
            )}
        </motion.div>
    );
}